/**
 * WebsiteTab.jsx — Tenant public website builder.
 *
 * Three-step wizard + content editors:
 *   1. Pick template (5)
 *   2. Pick palette (8 + accent swatches for pure_mono)
 *   3. Toggle sections (16, with 4 locked + auto-on rules)
 *
 * Plus AI Why-Choose-Us generator, opening hours editor, contact links,
 * and a Save Draft / Publish flow.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
    Globe, Wand2, Palette as PaletteIcon, Sparkles, Loader2, Save,
    Rocket, ExternalLink, Check, Lock, AlertCircle, Clock,
    Phone, MessageCircle, Facebook, Instagram, Youtube, MapPin,
    Plus, Trash2, RefreshCw, Eye, Users, Image as ImageIcon, HelpCircle, Video, Upload, ChevronDown
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../../components/ui/dialog';
import { useToast } from '../../../hooks/use-toast';
import {
    PALETTES, DEFAULT_PALETTE_ID, getPalette
} from '../../../components/tenant/palettes';
import {
    TEMPLATES, DEFAULT_TEMPLATE_ID, SECTION_KEYS,
    LOCKED_SECTIONS, SECTION_DEFAULTS, SECTION_LABELS
} from '../../../components/tenant/templates';
import WhyChooseUsBuilder from './WhyChooseUsBuilder';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mirrors FAQ_FALLBACK in components/tenant/sections/Closing.jsx — keep in sync.
const DEFAULT_FAQ = [
    { q: 'Do you offer warranty on repairs?', q_bn: 'মেরামতে কি ওয়ারেন্টি দেওয়া হয়?', a: 'Yes — every repair carries a workshop warranty. Ask the desk for terms.', a_bn: 'হ্যাঁ — প্রতিটি মেরামতে ওয়ার্কশপ ওয়ারেন্টি দেওয়া হয়। ডেস্কে শর্তাবলী জানতে পারবেন।' },
    { q: 'How long does a service take?', q_bn: 'সার্ভিসে কতক্ষণ সময় লাগে?', a: 'Standard service is usually 60–90 minutes. Heavy repairs are quoted before work begins.', a_bn: 'সাধারণ সার্ভিস ৬০–৯০ মিনিট। বড় কাজের জন্য আগে কোটেশন দেওয়া হয়।' },
    { q: 'Do you use genuine parts?', q_bn: 'কি জেনুইন পার্টস ব্যবহার করেন?', a: 'We default to genuine OEM parts. Aftermarket options are offered transparently when requested.', a_bn: 'আমরা সাধারণত জেনুইন OEM পার্টস ব্যবহার করি। অনুরোধে অন্য বিকল্পও স্বচ্ছভাবে দেখানো হয়।' },
    { q: "Can I track my bike's status?", q_bn: 'আমি কি বাইকের স্ট্যাটাস ট্র্যাক করতে পারি?', a: 'Yes — use the live tracker above with your phone and job number.', a_bn: 'হ্যাঁ — উপরের লাইভ ট্র্যাকারে ফোন ও জব নম্বর দিয়ে দেখুন।' }
];

const DAYS = [
    { key: 'mon', en: 'Mon', bn: 'সোম' },
    { key: 'tue', en: 'Tue', bn: 'মঙ্গল' },
    { key: 'wed', en: 'Wed', bn: 'বুধ' },
    { key: 'thu', en: 'Thu', bn: 'বৃহঃ' },
    { key: 'fri', en: 'Fri', bn: 'শুক্র' },
    { key: 'sat', en: 'Sat', bn: 'শনি' },
    { key: 'sun', en: 'Sun', bn: 'রবি' }
];

const CONVENIENCE_KEYS = [
    { key: 'pickup_drop', en: 'Pickup & Drop', bn: 'পিকআপ ও ড্রপ' },
    { key: 'express_lane', en: 'Express Lane', bn: 'এক্সপ্রেস লেন' },
    { key: 'emergency_24x7', en: '24/7 Emergency', bn: '২৪/৭ ইমার্জেন্সি' },
    { key: 'free_wash', en: 'Free Wash', bn: 'ফ্রি ওয়াশ' },
    { key: 'lounge', en: 'Customer Lounge', bn: 'কাস্টমার লাউঞ্জ' },
    { key: 'female_friendly', en: 'Female-Friendly', bn: 'নারীবান্ধব' }
];

export default function WebsiteTab({ language = 'en', hideHeaderActions = false, onActionsChange }) {
    const { toast } = useToast();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [tenant, setTenant] = useState(null);
    const [draft, setDraft] = useState(null);
    const [baseline, setBaseline] = useState(null);

    // Load current tenant settings (draft state — uses /settings/garage which
    // returns the full tenant doc with unpublished edits intact)
    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/settings/garage`)
            .then(res => {
                if (cancelled) return;
                setTenant(res.data);
                const initial = buildInitialDraft(res.data);
                setDraft(initial);
                setBaseline(JSON.stringify(sanitize(initial)));
            })
            .catch(() => {
                if (!cancelled) {
                    const initial = buildInitialDraft({});
                    setDraft(initial);
                    setBaseline(JSON.stringify(sanitize(initial)));
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const dirty = useMemo(
        () => draft && baseline ? JSON.stringify(sanitize(draft)) !== baseline : false,
        [draft, baseline]
    );

    const slug = tenant?.slug;
    const previewHref = slug ? `/${slug}` : null;

    // Expose actions/state to the parent so a unified action bar can drive them.
    // Declared before the early return so hook order stays stable.
    useEffect(() => {
        if (typeof onActionsChange !== 'function') return;
        onActionsChange({
            saveDraft,
            publish,
            previewHref,
            dirty,
            saving,
            publishing,
            ready: !loading
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dirty, saving, publishing, previewHref, loading]);

    if (loading || !draft) {
        return (
            <div className="flex items-center justify-center py-20 text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading website builder…
            </div>
        );
    }

    function update(patch) { setDraft(d => ({ ...d, ...patch })); }
    function updateSection(key, val) {
        setDraft(d => ({
            ...d,
            sections_enabled: { ...(d.sections_enabled || {}), [key]: val }
        }));
    }
    function updateConvenience(key, patch) {
        setDraft(d => ({
            ...d,
            convenience_promises: {
                ...(d.convenience_promises || {}),
                [key]: { ...(d.convenience_promises?.[key] || {}), ...patch }
            }
        }));
    }
    function updateOpeningHours(day, patch) {
        setDraft(d => ({
            ...d,
            opening_hours: {
                ...(d.opening_hours || {}),
                [day]: { ...(d.opening_hours?.[day] || { open: '09:00', close: '20:00', closed: false }), ...patch }
            }
        }));
    }


    async function saveDraft() {
        setSaving(true);
        try {
            const payload = sanitize(draft);
            await axios.put(`${API}/settings/garage`, payload);
            setBaseline(JSON.stringify(payload));
            toast({ title: 'Saved', description: 'Draft saved. Click Publish to go live.' });
        } catch (err) {
            toast({
                title: 'Save failed',
                description: formatApiError(err) || 'Could not save.',
                variant: 'destructive'
            });
        } finally {
            setSaving(false);
        }
    }

    async function publish() {
        setPublishing(true);
        try {
            const payload = sanitize(draft);
            await axios.put(`${API}/settings/garage`, payload);
            await axios.post(`${API}/settings/site/publish`, { draft: payload });
            setBaseline(JSON.stringify(payload));
            toast({ title: 'Published', description: 'Your site is live.' });
        } catch (err) {
            toast({
                title: 'Publish failed',
                description: formatApiError(err) || 'Could not publish.',
                variant: 'destructive'
            });
        } finally {
            setPublishing(false);
        }
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Globe className="w-6 h-6 text-blue-600" />
                        {language === 'en' ? 'Website Builder' : 'ওয়েবসাইট বিল্ডার'}
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        {language === 'en'
                            ? 'Pick a template, palette, sections and content. Save as draft, then publish when ready.'
                            : 'টেমপ্লেট, প্যালেট, সেকশন বাছুন। প্রথমে ড্রাফট, তারপর প্রকাশ।'}
                    </p>
                </div>
                {!hideHeaderActions && (
                    <div className="flex gap-2">
                        {previewHref && (
                            <Button asChild variant="outline" size="sm">
                                <a href={previewHref} target="_blank" rel="noopener noreferrer">
                                    <Eye className="w-4 h-4 mr-1.5" /> Preview
                                    <ExternalLink className="w-3 h-3 ml-1" />
                                </a>
                            </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={saveDraft} disabled={saving || !dirty}>
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                            Save draft
                        </Button>
                        <Button size="sm" onClick={publish} disabled={publishing} className="bg-emerald-600 hover:bg-emerald-700">
                            {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Rocket className="w-4 h-4 mr-1.5" />}
                            Publish
                        </Button>
                    </div>
                )}
            </div>

            {/* Step 1: Template */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <Wand2 className="w-5 h-5 text-blue-600" />
                        Step 1 — Template
                    </CardTitle>
                    <CardDescription>Choose a layout style. Sections and motion adapt automatically.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {TEMPLATES.map(t => {
                            const active = draft.templateStyle === t.id;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => update({ templateStyle: t.id })}
                                    className={[
                                        'group relative p-4 rounded-xl border text-left transition',
                                        active
                                            ? 'border-blue-600 ring-2 ring-blue-600/20 bg-blue-50/50'
                                            : 'border-slate-200 hover:border-blue-300 bg-white'
                                    ].join(' ')}
                                >
                                    {active && <Check className="absolute top-2 right-2 w-4 h-4 text-blue-600" />}
                                    <div className="font-semibold text-slate-900">{t.name}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">{t.tagline}</div>
                                    <div className="mt-2 text-xs text-slate-500 line-clamp-2">
                                        {t.uses3D ? '3D scene · ' : ''}{t.motionIntensity} motion
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Step 2: Palette */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <PaletteIcon className="w-5 h-5 text-blue-600" />
                        Step 2 — Palette
                    </CardTitle>
                    <CardDescription>Pick your brand colors. All palettes are contrast-checked.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {PALETTES.map(p => {
                            const active = draft.palette === p.id;
                            return (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => update({ palette: p.id })}
                                    className={[
                                        'relative p-4 rounded-xl border text-left transition',
                                        active
                                            ? 'border-blue-600 ring-2 ring-blue-600/20'
                                            : 'border-slate-200 hover:border-blue-300'
                                    ].join(' ')}
                                >
                                    {active && <Check className="absolute top-2 right-2 w-4 h-4 text-blue-600" />}
                                    <div className="flex gap-1 mb-3">
                                        {[p.surface, p.primary, p.accent || p.primary_soft, p.ink].map((c, i) => (
                                            <div key={i} className="w-8 h-8 rounded-lg border border-slate-200" style={{ background: c }} />
                                        ))}
                                    </div>
                                    <div className="font-semibold text-sm text-slate-900">{p.name}</div>
                                    {p.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{p.description}</div>}
                                </button>
                            );
                        })}
                    </div>

                    {(() => {
                        const cur = getPalette(draft.palette);
                        if (!cur?.accentSwatches) return null;
                        return (
                            <div className="pt-2">
                                <Label className="text-xs uppercase tracking-wider text-slate-500">Accent swatch (Pure Mono)</Label>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {cur.accentSwatches.map(sw => {
                                        const id = sw.id;
                                        const hex = sw.value;
                                        const label = sw.label || id;
                                        return (
                                            <button
                                                key={id}
                                                type="button"
                                                onClick={() => update({ accentSwatch: id })}
                                                className={[
                                                    'flex items-center gap-2 px-3 h-9 rounded-full border text-sm transition',
                                                    draft.accentSwatch === id ? 'border-blue-600 ring-2 ring-blue-600/20' : 'border-slate-200 hover:border-slate-400'
                                                ].join(' ')}
                                            >
                                                <span className="w-4 h-4 rounded-full" style={{ background: hex }} />
                                                <span>{label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })()}
                </CardContent>
            </Card>

            {/* Step 3: Sections */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <Sparkles className="w-5 h-5 text-blue-600" />
                        Step 3 — Sections
                    </CardTitle>
                    <CardDescription>
                        Toggle sections on/off. Locked sections are required. Some auto-enable when you add content.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                        {SECTION_KEYS.map(key => {
                            const locked = LOCKED_SECTIONS.has(key);
                            const enabled = locked || (draft.sections_enabled?.[key] ?? SECTION_DEFAULTS[key]);
                            const label = SECTION_LABELS[key];
                            return (
                                <div key={key} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-b-0">
                                    <div className="flex items-center gap-2 min-w-0">
                                        {locked && <Lock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
                                        <span className="text-sm font-medium text-slate-800 truncate">
                                            {language === 'bn' ? label.bn : label.en}
                                        </span>
                                    </div>
                                    <Switch
                                        checked={!!enabled}
                                        disabled={locked}
                                        onCheckedChange={v => updateSection(key, v)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Wizard Why Choose Us — questionnaire-driven */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-3">
                    <div>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Sparkles className="w-5 h-5 text-amber-500" />
                            Why Choose Us — built with our wizard
                        </CardTitle>
                        <CardDescription>
                            Answer a quick questionnaire. Our wizard generates 8 cards. You pick 4–6 to publish, and edit any text any time.
                        </CardDescription>
                    </div>
                    <WhyChooseUsBuilder
                        tenant={tenant}
                        selected={draft.why_choose_us}
                        library={draft.why_choose_us_library || tenant?.why_choose_us_library || tenant?.why_choose_us_draft}
                        answers={draft.why_choose_us_answers || tenant?.why_choose_us_answers}
                        onApply={(cards, extras) => update({
                            why_choose_us: cards,
                            ...(extras?.library ? { why_choose_us_library: extras.library } : {}),
                            ...(extras?.answers ? { why_choose_us_answers: extras.answers } : {})
                        })}
                        formatApiError={formatApiError}
                    />
                </CardHeader>
                <CardContent>
                    {(!draft.why_choose_us || draft.why_choose_us.length === 0) ? (
                        <div className="text-sm text-slate-500 italic">
                            No cards yet. Click <span className="font-medium not-italic text-slate-700">Build with wizard</span> to start the questionnaire.
                        </div>
                    ) : (
                        <>
                            <div className="text-xs text-slate-500 mb-2">
                                {draft.why_choose_us.length} card{draft.why_choose_us.length === 1 ? '' : 's'} will appear on your live site. Click <span className="font-medium text-slate-700">Edit cards</span> to swap or rewrite — your library is saved, no need to regenerate.
                            </div>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {draft.why_choose_us.map((c, i) => (
                                    <div key={i} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                                        <div className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold">{c.icon}</div>
                                        <div className="font-semibold text-sm text-slate-900 mt-1">{c.title}</div>
                                        {c.title_bn && <div className="text-xs text-slate-700 font-bn">{c.title_bn}</div>}
                                        <div className="text-xs text-slate-600 mt-1.5 line-clamp-3">{c.body}</div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>



            {/* Convenience promises */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg">Convenience promises</CardTitle>
                    <CardDescription>Toggle the perks you offer. They show up as a dedicated section.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {CONVENIENCE_KEYS.map(c => {
                            const cur = draft.convenience_promises?.[c.key] || {};
                            return (
                                <div key={c.key} className="p-3 rounded-lg border border-slate-200">
                                    <div className="flex items-center justify-between">
                                        <div className="font-medium text-sm">{c.en}</div>
                                        <Switch
                                            checked={!!cur.enabled}
                                            onCheckedChange={v => updateConvenience(c.key, { enabled: v })}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Team / Gallery / FAQ / Videos editors (collapsible) */}
            <ListEditorCard
                title="Team members"
                description="Show your mechanics and front desk. Auto-hidden if empty."
                icon={<Users className="w-5 h-5 text-blue-600" />}
                items={draft.team_members || []}
                onChange={(arr) => update({ team_members: arr })}
                render={(item, on) => (
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Name"><Input value={item.name || ''} onChange={e => on({ name: e.target.value })} /></Field>
                        <Field label="Role"><Input value={item.role || ''} onChange={e => on({ role: e.target.value })} placeholder="Senior mechanic" /></Field>
                        <Field label="Years of experience"><Input type="number" value={item.years_exp ?? ''} onChange={e => on({ years_exp: e.target.value === '' ? '' : Number(e.target.value) })} /></Field>
                        <Field label="Specialty"><Input value={item.specialty || ''} onChange={e => on({ specialty: e.target.value })} placeholder="Engine rebuilds" /></Field>
                        <div className="sm:col-span-2">
                            <ImageField label="Photo" value={item.photo_url} onChange={(url) => on({ photo_url: url })} />
                        </div>
                    </div>
                )}
                newItem={() => ({ name: '', role: '', years_exp: '', specialty: '', photo_url: '' })}
                summary={(it) => it.name || 'New member'}
            />

            <ListEditorCard
                title="Gallery (Before / After)"
                description="Paired before/after photos. Auto-hidden if empty."
                icon={<ImageIcon className="w-5 h-5 text-blue-600" />}
                items={draft.before_after_gallery || []}
                onChange={(arr) => update({ before_after_gallery: arr })}
                render={(item, on) => (
                    <div className="space-y-3">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <ImageField label="Before" value={item.before_url} onChange={(url) => on({ before_url: url })} />
                            <ImageField label="After" value={item.after_url} onChange={(url) => on({ after_url: url })} />
                        </div>
                        <Field label="Caption"><Input value={item.caption || ''} onChange={e => on({ caption: e.target.value })} placeholder="Engine overhaul" /></Field>
                        <Field label="Caption (Bangla)"><Input value={item.caption_bn || ''} onChange={e => on({ caption_bn: e.target.value })} /></Field>
                    </div>
                )}
                newItem={() => ({ before_url: '', after_url: '', caption: '', caption_bn: '' })}
                summary={(it) => it.caption || 'Untitled pair'}
            />

            <ListEditorCard
                title="FAQ"
                description="Questions answered on the public site. Falls back to defaults when empty."
                icon={<HelpCircle className="w-5 h-5 text-blue-600" />}
                items={draft.faq || []}
                onChange={(arr) => update({ faq: arr })}
                render={(item, on) => (
                    <div className="grid gap-3">
                        <Field label="Question (English)"><Input value={item.q || ''} onChange={e => on({ q: e.target.value })} /></Field>
                        <Field label="প্রশ্ন (বাংলা)"><Input value={item.q_bn || ''} onChange={e => on({ q_bn: e.target.value })} /></Field>
                        <Field label="Answer (English)"><Textarea rows={3} value={item.a || ''} onChange={e => on({ a: e.target.value })} /></Field>
                        <Field label="উত্তর (বাংলা)"><Textarea rows={3} value={item.a_bn || ''} onChange={e => on({ a_bn: e.target.value })} /></Field>
                    </div>
                )}
                newItem={() => ({ q: '', q_bn: '', a: '', a_bn: '' })}
                summary={(it) => it.q || 'New question'}
            />

            <ListEditorCard
                title="Featured videos"
                description="YouTube links for the RealWorkReel section. Auto-hidden if empty."
                icon={<Video className="w-5 h-5 text-blue-600" />}
                items={draft.featured_videos || []}
                onChange={(arr) => update({ featured_videos: arr })}
                render={(item, on) => (
                    <div className="grid gap-3">
                        <Field label="YouTube URL"><Input value={item.youtube_url || ''} onChange={e => on({ youtube_url: e.target.value })} placeholder="https://youtu.be/..." /></Field>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <Field label="Title"><Input value={item.title || ''} onChange={e => on({ title: e.target.value })} /></Field>
                            <Field label="Duration"><Input value={item.duration || ''} onChange={e => on({ duration: e.target.value })} placeholder="2:30" /></Field>
                        </div>
                        <ImageField label="Custom thumbnail (optional)" value={item.thumbnail_url} onChange={(url) => on({ thumbnail_url: url })} />
                    </div>
                )}
                newItem={() => ({ youtube_url: '', title: '', duration: '', thumbnail_url: '' })}
                summary={(it) => it.title || it.youtube_url || 'New video'}
            />

            {/* Opening hours */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Clock className="w-5 h-5 text-blue-600" /> Opening hours
                    </CardTitle>
                    <CardDescription>Used by booking slot generator + map section + JSON-LD.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        {DAYS.map(d => {
                            const h = draft.opening_hours?.[d.key] || { open: '09:00', close: '20:00', closed: false };
                            return (
                                <div key={d.key} className="grid grid-cols-12 items-center gap-2 text-sm">
                                    <div className="col-span-2 font-medium text-slate-700">{d.en}</div>
                                    <div className="col-span-2 flex items-center gap-2">
                                        <Switch checked={!h.closed} onCheckedChange={v => updateOpeningHours(d.key, { closed: !v })} />
                                        <span className="text-xs text-slate-500">{h.closed ? 'Closed' : 'Open'}</span>
                                    </div>
                                    <Input
                                        type="time"
                                        className="col-span-3"
                                        value={h.open}
                                        disabled={h.closed}
                                        onChange={e => updateOpeningHours(d.key, { open: e.target.value })}
                                    />
                                    <span className="col-span-1 text-center text-slate-400">—</span>
                                    <Input
                                        type="time"
                                        className="col-span-3"
                                        value={h.close}
                                        disabled={h.closed}
                                        onChange={e => updateOpeningHours(d.key, { close: e.target.value })}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Contact / social */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg">Social channels & service area</CardTitle>
                    <CardDescription>WhatsApp drives the hero CTA + parts inquiry. Phone, name &amp; address are managed in the section above.</CardDescription>
                </CardHeader>
                <CardContent className="grid sm:grid-cols-2 gap-4">
                    <Field icon={<MessageCircle className="w-4 h-4" />} label="WhatsApp number">
                        <Input value={draft.whatsapp_number || ''} onChange={e => update({ whatsapp_number: e.target.value })} />
                    </Field>
                    <Field icon={<MapPin className="w-4 h-4" />} label="Service area (e.g. Dhanmondi, Dhaka)">
                        <Input value={draft.area_served || ''} onChange={e => update({ area_served: e.target.value })} />
                    </Field>
                    <Field icon={<Facebook className="w-4 h-4" />} label="Facebook URL">
                        <Input value={draft.facebook_url || ''} onChange={e => update({ facebook_url: e.target.value })} />
                    </Field>
                    <Field icon={<Instagram className="w-4 h-4" />} label="Instagram URL">
                        <Input value={draft.instagram_url || ''} onChange={e => update({ instagram_url: e.target.value })} />
                    </Field>
                    <Field icon={<Youtube className="w-4 h-4" />} label="YouTube channel URL">
                        <Input value={draft.youtube_url || ''} onChange={e => update({ youtube_url: e.target.value })} />
                    </Field>
                </CardContent>
            </Card>

            {/* Sticky footer save bar — hidden when parent owns the action bar */}
            {!hideHeaderActions && (
                <div className="sticky bottom-0 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-white/80 backdrop-blur border-t border-slate-200 flex items-center justify-between gap-3 z-10">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Changes apply only after Publish.
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={saveDraft} disabled={saving}>
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                            Save draft
                        </Button>
                        <Button size="sm" onClick={publish} disabled={publishing} className="bg-emerald-600 hover:bg-emerald-700">
                            {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Rocket className="w-4 h-4 mr-1.5" />}
                            Publish
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ icon, label, children }) {
    return (
        <div>
            <Label className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500">
                {icon} {label}
            </Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}

/**
 * ImageField — small uploader that POSTs to /api/upload/image?image_type=asset
 * and stores the returned data URI in the field. Caller is responsible for
 * persisting the URL inside the array entry.
 */
function ImageField({ label, value, onChange }) {
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const { toast } = useToast();
    async function pick(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await axios.post(`${API}/upload/image?image_type=asset`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            onChange(res.data?.url || '');
        } catch (err) {
            toast({ title: 'Upload failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    }
    return (
        <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
            <div className="mt-1.5 flex items-center gap-3">
                {value ? (
                    <img src={value} alt="" className="w-16 h-16 rounded-md object-cover border border-slate-200" />
                ) : (
                    <div className="w-16 h-16 rounded-md bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                        <ImageIcon className="w-5 h-5" />
                    </div>
                )}
                <div className="flex-1 flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => fileRef.current?.click()} className="gap-1.5">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {value ? 'Replace' : 'Upload'}
                    </Button>
                    {value && (
                        <Button type="button" variant="outline" size="sm" onClick={() => onChange('')} className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 gap-1.5">
                            <Trash2 className="w-4 h-4" /> Clear
                        </Button>
                    )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={pick} />
            </div>
        </div>
    );
}

/**
 * ListEditorCard — collapsible card with add/remove/reorder rows. Pass a
 * `render(item, onPatch)` to render each entry. `newItem()` produces a blank
 * row, `summary(item)` produces the collapsed-row label.
 */
function ListEditorCard({ title, description, icon, items, onChange, render, newItem, summary }) {
    const [open, setOpen] = useState(false);
    const [openRow, setOpenRow] = useState(null);
    function add() {
        const next = [...(items || []), newItem()];
        onChange(next);
        setOpenRow(next.length - 1);
        setOpen(true);
    }
    function patch(i, p) {
        const next = items.map((it, idx) => idx === i ? { ...it, ...p } : it);
        onChange(next);
    }
    function remove(i) {
        onChange(items.filter((_, idx) => idx !== i));
    }
    function move(i, dir) {
        const j = i + dir;
        if (j < 0 || j >= items.length) return;
        const next = [...items];
        [next[i], next[j]] = [next[j], next[i]];
        onChange(next);
    }
    return (
        <Card>
            <Collapsible open={open} onOpenChange={setOpen}>
                <CollapsibleTrigger asChild>
                    <button type="button" className="w-full text-left">
                        <CardHeader>
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex-1">
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        {icon}
                                        {title}
                                        <span className="ml-1 inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">
                                            {(items || []).length}
                                        </span>
                                    </CardTitle>
                                    {description && <CardDescription>{description}</CardDescription>}
                                </div>
                                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                            </div>
                        </CardHeader>
                    </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <CardContent className="space-y-3">
                        {(items || []).map((it, i) => (
                            <div key={i} className="border border-slate-200 rounded-lg overflow-hidden">
                                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
                                    <button type="button" className="flex-1 text-left text-sm font-medium text-slate-700 truncate" onClick={() => setOpenRow(openRow === i ? null : i)}>
                                        {summary(it) || `Item ${i + 1}`}
                                    </button>
                                    <Button type="button" size="sm" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} className="h-7 w-7 p-0">↑</Button>
                                    <Button type="button" size="sm" variant="ghost" disabled={i === items.length - 1} onClick={() => move(i, 1)} className="h-7 w-7 p-0">↓</Button>
                                    <Button type="button" size="sm" variant="ghost" onClick={() => setOpenRow(openRow === i ? null : i)} className="h-7 px-2 text-xs">
                                        {openRow === i ? 'Close' : 'Edit'}
                                    </Button>
                                    <Button type="button" size="sm" variant="ghost" onClick={() => remove(i)} className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50">
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                                {openRow === i && (
                                    <div className="p-4 bg-white">
                                        {render(it, (p) => patch(i, p))}
                                    </div>
                                )}
                            </div>
                        ))}
                        <Button type="button" variant="outline" size="sm" onClick={add} className="gap-1.5">
                            <Plus className="w-4 h-4" /> Add
                        </Button>
                    </CardContent>
                </CollapsibleContent>
            </Collapsible>
        </Card>
    );
}

function buildInitialDraft(t = {}) {
    return {
        templateStyle: t.template_style || t.templateStyle || DEFAULT_TEMPLATE_ID,
        palette: t.palette_id || t.palette || DEFAULT_PALETTE_ID,
        accentSwatch: t.accent_swatch || t.accentSwatch || null,
        sections_enabled: t.enabled_sections || t.sections_enabled || {},
        tagline: t.tagline || '',
        tagline_bn: t.tagline_bn || '',
        about: t.about || '',
        about_bn: t.about_bn || '',
        why_choose_us: Array.isArray(t.why_choose_us) ? t.why_choose_us : [],
        // Library = full pool of 8 cards from the wizard, kept so the owner
        // can swap selections later without re-generating. Falls back to the
        // legacy `why_choose_us_draft` field for tenants that pre-date the
        // dedicated library column.
        why_choose_us_library: Array.isArray(t.why_choose_us_library)
            ? t.why_choose_us_library
            : (Array.isArray(t.why_choose_us_draft) ? t.why_choose_us_draft : []),
        why_choose_us_answers: t.why_choose_us_answers || null,
        team_members: Array.isArray(t.team_members) ? t.team_members : [],
        before_after_gallery: Array.isArray(t.before_after_gallery) ? t.before_after_gallery : [],
        featured_videos: Array.isArray(t.featured_videos) ? t.featured_videos : [],
        faq: Array.isArray(t.faq) && t.faq.length > 0 ? t.faq : DEFAULT_FAQ.map(x => ({ ...x })),
        convenience_promises: listToDict(t.convenience_promises, 'key') || {},
        opening_hours: listToDict(t.opening_hours, 'day') || {
            mon: { open: '09:00', close: '20:00', closed: false },
            tue: { open: '09:00', close: '20:00', closed: false },
            wed: { open: '09:00', close: '20:00', closed: false },
            thu: { open: '09:00', close: '20:00', closed: false },
            fri: { open: '14:00', close: '20:00', closed: false },
            sat: { open: '09:00', close: '20:00', closed: false },
            sun: { open: '09:00', close: '18:00', closed: false }
        },
        phone: t.phone || '',
        whatsapp_number: t.whatsapp_number || '',
        facebook_url: t.facebook_url || '',
        instagram_url: t.instagram_url || '',
        youtube_url: t.youtube_url || '',
        area_served: t.area_served || '',
        latitude: t.latitude,
        longitude: t.longitude
    };
}

function listToDict(v, idKey) {
    if (!v) return null;
    if (!Array.isArray(v)) return v; // already dict
    const out = {};
    v.forEach(item => {
        if (!item || typeof item !== 'object') return;
        const id = item[idKey];
        if (!id) return;
        const { [idKey]: _omit, ...rest } = item;
        out[id] = rest;
    });
    return out;
}

function sanitize(d) {
    // Strip undefined / empty-string controlled values for cleaner update payload.
    // Coerce numeric fields (lat/lng) so '' or invalid inputs don't 422 on the server.
    // Convert dict-shaped UI state for opening_hours / convenience_promises into
    // the List[Dict] shape the backend Pydantic model expects.
    // Rename UI camelCase keys to backend snake_case so theme/palette actually persist.
    const NUMERIC_KEYS = new Set(['latitude', 'longitude']);
    const DICT_TO_LIST = {
        opening_hours: 'day',
        convenience_promises: 'key'
    };
    const RENAME = {
        templateStyle: 'template_style',
        palette: 'palette_id',
        accentSwatch: 'accent_swatch',
        sections_enabled: 'enabled_sections'
    };
    const out = {};
    Object.entries(d).forEach(([k, v]) => {
        if (v === undefined) return;
        const targetKey = RENAME[k] || k;
        if (NUMERIC_KEYS.has(k)) {
            if (v === '' || v === null) return;
            const n = typeof v === 'number' ? v : parseFloat(v);
            if (Number.isFinite(n)) out[targetKey] = n;
            return;
        }
        if (DICT_TO_LIST[k] && v && !Array.isArray(v) && typeof v === 'object') {
            const idKey = DICT_TO_LIST[k];
            out[targetKey] = Object.entries(v).map(([id, val]) => ({
                [idKey]: id,
                ...(val && typeof val === 'object' ? val : { value: val })
            }));
            return;
        }
        out[targetKey] = v;
    });
    return out;
}

// Render FastAPI 422 detail (list of {type, loc, msg, ...}) as a readable string
// so React doesn't choke on raw objects when shown in a toast.
function formatApiError(err) {
    const detail = err?.response?.data?.detail;
    if (!detail) return err?.message || '';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        return detail
            .map(d => {
                if (!d || typeof d !== 'object') return String(d);
                const field = Array.isArray(d.loc) ? d.loc.filter(x => x !== 'body').join('.') : '';
                return [field, d.msg].filter(Boolean).join(': ');
            })
            .filter(Boolean)
            .join(' • ');
    }
    if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
    return String(detail);
}
